import type { SlideInfo } from '@/components/DeckViewer'

/** Open speaker notes in a separate window with BroadcastChannel sync */
export function openSpeakerNotesWindow(slides: SlideInfo[], currentSlide: number) {
  // Escape `<` so slide content containing `</script>` can't break out of the
  // inline <script> below (slides come from deck HTML, but harden anyway).
  const slidesJson = JSON.stringify(slides.map(s => ({
    index: s.index,
    label: s.label,
    title: s.title,
    notes: s.notes,
  }))).replace(/</g, '\\u003c')
  const startSlide = Number(currentSlide) | 0

  const win = window.open('', '_blank', 'width=640,height=700')
  if (!win) return

  win.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Speaker Notes — GC Slides</title>
  <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&family=Bricolage+Grotesque:opsz,wght@12..96,200..800&display=swap" rel="stylesheet">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: Roboto, Arial, sans-serif;
      background: #141517; color: #fff;
      padding: 32px 28px; min-height: 100vh;
      display: flex; flex-direction: column;
    }
    .sn-header {
      display: flex; align-items: center; justify-content: space-between;
      margin-bottom: 20px;
    }
    .sn-slide-num {
      font-size: 14px; font-weight: 700; color: #FF5A2C;
      font-variant-numeric: tabular-nums; letter-spacing: 0.5px;
    }
    .sn-timer {
      font-size: 13px; color: #666;
      font-variant-numeric: tabular-nums;
      font-family: 'Roboto Mono', monospace;
    }
    .sn-label {
      font-size: 11px; font-weight: 700; letter-spacing: 2px;
      color: #FF5A2C; text-transform: uppercase;
      margin-bottom: 6px;
    }
    .sn-title {
      font-family: 'Bricolage Grotesque', sans-serif;
      font-size: 22px; font-weight: 600; color: #fff;
      line-height: 1.3; margin-bottom: 20px;
    }
    .sn-divider {
      width: 100%; height: 1px;
      background: rgba(255, 255, 255, 0.08);
      margin: 16px 0;
    }
    .sn-notes {
      font-size: 20px; color: rgba(255, 255, 255, 0.85);
      line-height: 1.7; white-space: pre-line;
      flex: 1;
    }
    .sn-empty {
      font-size: 16px; color: rgba(255, 255, 255, 0.35);
      font-style: italic; flex: 1;
    }
    .sn-next-preview {
      margin-top: 20px; padding: 14px 18px;
      background: rgba(255, 255, 255, 0.03);
      border: 1px solid rgba(255, 255, 255, 0.06);
      border-radius: 8px;
    }
    .sn-next-label {
      font-size: 10px; font-weight: 700; letter-spacing: 2px;
      color: #666; text-transform: uppercase; margin-bottom: 4px;
    }
    .sn-next-title {
      font-size: 15px; color: rgba(255, 255, 255, 0.5);
    }
    .sn-nav {
      display: flex; align-items: center; justify-content: space-between;
      margin-top: 24px; padding-top: 16px;
      border-top: 1px solid rgba(255, 255, 255, 0.06);
    }
    .sn-nav-btn {
      padding: 10px 24px; border-radius: 6px;
      border: 1px solid rgba(255, 255, 255, 0.15);
      background: rgba(255, 255, 255, 0.05);
      font-size: 14px; font-weight: 600; color: #ccc;
      cursor: pointer; font-family: Roboto, sans-serif;
      transition: all 0.15s;
    }
    .sn-nav-btn:hover:not(:disabled) { border-color: #FF5A2C; color: #FF5A2C; }
    .sn-nav-btn:disabled { opacity: 0.25; cursor: default; }
  </style>
</head>
<body>
  <div class="sn-header">
    <span class="sn-slide-num" id="slideNum"></span>
    <span class="sn-timer" id="timer">00:00</span>
  </div>
  <div class="sn-label" id="slideLabel"></div>
  <div class="sn-title" id="slideTitle"></div>
  <div class="sn-divider"></div>
  <div class="sn-notes" id="slideNotes"></div>
  <div class="sn-next-preview" id="nextPreview">
    <div class="sn-next-label">UP NEXT</div>
    <div class="sn-next-title" id="nextTitle"></div>
  </div>
  <div class="sn-nav">
    <button class="sn-nav-btn" id="prevBtn">&larr; Prev</button>
    <button class="sn-nav-btn" id="nextBtn">Next &rarr;</button>
  </div>

  <script>
    const slides = ${slidesJson};
    let current = ${startSlide};
    const startTime = Date.now();

    const channel = new BroadcastChannel('gc-slides-sync');

    function render() {
      const slide = slides[current] || {};
      const notes = (slide.notes || '').replace(/\\\\n/g, '\\n');
      document.getElementById('slideNum').textContent = 'Slide ' + (current + 1) + ' / ' + slides.length;
      document.getElementById('slideLabel').textContent = slide.label || '';
      document.getElementById('slideTitle').textContent = slide.title || ('Slide ' + (current + 1));

      const notesEl = document.getElementById('slideNotes');
      if (notes) {
        notesEl.textContent = notes;
        notesEl.className = 'sn-notes';
      } else {
        notesEl.textContent = 'No speaker notes for this slide.';
        notesEl.className = 'sn-empty';
      }

      const nextSlide = slides[current + 1];
      const nextPreview = document.getElementById('nextPreview');
      if (nextSlide) {
        nextPreview.style.display = '';
        document.getElementById('nextTitle').textContent = nextSlide.title || ('Slide ' + (current + 2));
      } else {
        nextPreview.style.display = 'none';
      }

      document.getElementById('prevBtn').disabled = current <= 0;
      document.getElementById('nextBtn').disabled = current >= slides.length - 1;
    }

    function goTo(index) {
      if (index < 0 || index >= slides.length) return;
      current = index;
      render();
      channel.postMessage({ type: 'slide-change', index: current });
    }

    channel.onmessage = function(event) {
      if (event.data && event.data.type === 'slide-change') {
        current = event.data.index;
        render();
      }
    };

    document.getElementById('prevBtn').addEventListener('click', function() { goTo(current - 1); });
    document.getElementById('nextBtn').addEventListener('click', function() { goTo(current + 1); });

    document.addEventListener('keydown', function(e) {
      if (e.key === 'ArrowLeft') goTo(current - 1);
      if (e.key === 'ArrowRight') goTo(current + 1);
    });

    // Timer
    setInterval(function() {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const m = Math.floor(elapsed / 60).toString().padStart(2, '0');
      const s = (elapsed % 60).toString().padStart(2, '0');
      document.getElementById('timer').textContent = m + ':' + s;
    }, 1000);

    render();
  </script>
</body>
</html>`)
  win.document.close()
}
